import React, { useEffect, useState, useRef } from "react";
import { useI18n } from "../../i18n/I18nContext";

const useInView = (threshold = 0.3) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const Counter = ({ target, duration = 1400, suffix = "%" }) => {
  const [ref, inView] = useInView(0.3);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return (
    <span ref={ref} className="mono text-glow-cyan">
      {val}
      {suffix}
    </span>
  );
};

export const CrisisStats = () => {
  const { t } = useI18n();

  return (
    <section data-testid="crisis-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-5">
            <div className="mono text-xs uppercase tracking-[0.22em] text-brand-cyan mb-3">
              {t.crisis.sectionNum}
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              {t.crisis.headlinePart1}
              <br />
              <span className="text-muted">{t.crisis.headlinePart2}</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end">
            <p className="text-muted text-base leading-relaxed">
              {t.crisis.subtext}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {t.crisis.stats.map((s, idx) => (
            <div
              key={idx}
              data-testid={`crisis-stat-${idx + 1}`}
              className="relative glass rounded-2xl p-8 overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
            >
              <div
                className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-20"
                style={{ background: s.accent }}
              />
              <div
                className="text-7xl sm:text-8xl font-heading font-black tracking-tighter leading-none"
                style={{ color: s.accent }}
              >
                <Counter target={s.value} />
              </div>
              <div className="mt-6 font-heading text-lg font-semibold">{s.title}</div>
              <p className="mt-3 text-sm text-muted leading-relaxed">{s.body}</p>
              <div
                className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-700"
                style={{ background: `linear-gradient(90deg, ${s.accent}, transparent)` }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CrisisStats;
